import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Key, CreditCard, CheckCircle2, Loader2, Clock, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { docenteKeysApi } from '../lib/api';

const PACKAGE_HIGHLIGHTS = {
  key_1: { color: 'bg-gray-50', accent: 'border-gray-300' },
  key_10: { color: 'bg-blue-50', accent: 'border-blue-400', tag: 'Più scelto' },
  key_50: { color: 'bg-emerald-50', accent: 'border-emerald-400', tag: 'Miglior prezzo' },
  key_100: { color: 'bg-violet-50', accent: 'border-violet-500', tag: 'Maxi sconto' },
  sub_monthly: { color: 'bg-amber-50', accent: 'border-amber-400', tag: 'Illimitato' },
};

export default function DocenteKeysPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [purchasing, setPurchasing] = useState(null);
  const [checkoutStatusMsg, setCheckoutStatusMsg] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [keysRes, pkgRes] = await Promise.all([
        docenteKeysApi.myKeys(),
        docenteKeysApi.packages(),
      ]);
      setData(keysRes.data);
      setPackages(pkgRes.data);
    } catch (e) {
      toast.error('Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll checkout status if we are returning from Stripe
  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await docenteKeysApi.getCheckoutStatus(sessionId);
        if (res.data.payment_status === 'paid') {
          setCheckoutStatusMsg('Pagamento completato! Chiavi accreditate.');
          toast.success('Pagamento completato, chiavi accreditate ✅');
          await load();
          // clean URL
          window.history.replaceState({}, document.title, '/docente-keys');
          return;
        }
        if (res.data.status === 'expired') {
          setCheckoutStatusMsg('Sessione di pagamento scaduta.');
          toast.error('Sessione scaduta');
          window.history.replaceState({}, document.title, '/docente-keys');
          return;
        }
      } catch (e) {
        // continue polling
      }
      if (attempts < maxAttempts) {
        setCheckoutStatusMsg('Verifica pagamento in corso...');
        setTimeout(poll, 2500);
      } else {
        setCheckoutStatusMsg('Verifica scaduta. Controlla tra un minuto.');
        window.history.replaceState({}, document.title, '/docente-keys');
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePurchase = async (pkg) => {
    try {
      setPurchasing(pkg.id);
      const res = await docenteKeysApi.createCheckout({
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione pagamento');
      setPurchasing(null);
    }
  };

  const available = data?.available || 0;
  const used = data?.used || 0;
  const subscription = data?.subscription;
  const isSubscribed = !!subscription;

  return (
    <div className="space-y-6 p-6" data-testid="docente-keys-page">
      <div>
        <h1 className="text-3xl font-bold">Le mie Chiavi</h1>
        <p className="text-gray-600 mt-1">
          Ogni chiave ti permette di creare un registro presenze pubblico. Le chiavi non hanno scadenza.
        </p>
      </div>

      {checkoutStatusMsg && (
        <div className="border rounded-md p-3 bg-blue-50 border-blue-200 text-blue-900 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> {checkoutStatusMsg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-6 text-center">
            <Key className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
            <div className="text-4xl font-bold">{available}</div>
            <div className="text-sm text-gray-500 mt-1">Chiavi disponibili</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-6 text-center">
            <Ticket className="w-8 h-8 mx-auto text-gray-500 mb-2" />
            <div className="text-4xl font-bold">{used}</div>
            <div className="text-sm text-gray-500 mt-1">Chiavi utilizzate</div>
          </CardContent>
        </Card>
        <Card className={isSubscribed ? 'border-amber-400 bg-amber-50' : ''}>
          <CardContent className="py-6 text-center">
            <CheckCircle2 className={`w-8 h-8 mx-auto mb-2 ${isSubscribed ? 'text-amber-600' : 'text-gray-300'}`} />
            <div className="text-lg font-bold">
              {isSubscribed ? 'Attivo' : 'Non attivo'}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Abbonamento mensile
            </div>
            {isSubscribed && subscription.expires_at && (
              <div className="text-xs text-gray-500 mt-1">
                Scade: {new Date(subscription.expires_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-3">Acquista chiavi</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg) => {
            const hl = PACKAGE_HIGHLIGHTS[pkg.id] || {};
            return (
              <Card key={pkg.id} className={`${hl.color} ${hl.accent} border-2 relative`}>
                {hl.tag && (
                  <Badge className="absolute -top-2 right-3 bg-orange-500 text-white">
                    {hl.tag}
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-base">{pkg.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-1">
                    €{pkg.amount.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-600 mb-3">
                    {pkg.type === 'keys'
                      ? `€${(pkg.amount / pkg.quantity).toFixed(2)} / chiave`
                      : 'Chiavi illimitate per 30 giorni'}
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => handlePurchase(pkg)}
                    disabled={purchasing === pkg.id}
                    data-testid={`buy-${pkg.id}`}
                  >
                    {purchasing === pkg.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" /> Acquista
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-3">Storico chiavi</h2>
        {loading ? (
          <div className="text-center py-10 text-gray-500">Caricamento...</div>
        ) : (data?.keys || []).length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-gray-500">
              Nessuna chiave ancora. Acquista un pacchetto per iniziare.
            </CardContent>
          </Card>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codice chiave</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Origine</TableHead>
                <TableHead>Data acquisizione</TableHead>
                <TableHead>Data utilizzo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.keys.map((k) => (
                <TableRow key={k.id}>
                  <TableCell className="font-mono text-xs">{k.key_code}</TableCell>
                  <TableCell>
                    {k.status === 'unused' ? (
                      <Badge className="bg-emerald-100 text-emerald-800">Disponibile</Badge>
                    ) : (
                      <Badge className="bg-gray-200 text-gray-700">
                        <Clock className="w-3 h-3 mr-1" /> Usata
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {k.source === 'purchase'
                      ? `Acquisto (${k.package_id || ''})`
                      : k.source === 'admin_grant'
                      ? `Assegnata da admin`
                      : k.source || '-'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {k.created_at ? new Date(k.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {k.used_at ? new Date(k.used_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
